import type {
	BekreftOrdreResponse as GenBekreftOrdreResponse,
	KredittsjekkResponse as GenKredittsjekkResponse,
	Kvalitetssikring as GenKvalitetssikring,
	Maanedsvolum as GenMaanedsvolum,
	Sikring as GenSikring,
	SikringMaalepunkt as GenSikringMaalepunkt,
	Sikringskunde as GenSikringskunde,
	Sikringsordre as GenSikringsordre,
	ValidationError as GenValidationError,
} from "codegen";
import { Sikringsordrestatus as GenSikringsordrestatus } from "codegen";

export { SikringFlyt, SikringOmfangType, type CreateOrderWithSikringRequest, type CreateSikringRequest } from "codegen";

/**
 * Statusene fra codegen, pluss DELETED for soft-slettede ordrer. Lagt til her slik at typen ikke
 * avhenger av om backend-spesifikasjonen codegen kjøres mot har fått statusen ennå.
 */
export const Sikringsordrestatus = { ...GenSikringsordrestatus, Deleted: "DELETED" } as const;
export type Sikringsordrestatus = (typeof Sikringsordrestatus)[keyof typeof Sikringsordrestatus];

/** Gjør N til `felt | null` (påkrevd, men nullbar), resten av T til påkrevd og ikke-nullbar. */
type NullableFields<T, N extends keyof T> = Required<Omit<T, N>> & { [K in N]-?: T[K] | null };

/** Sikringen slik den ble bestilt. Datoene er rene datoer (yyyy-MM-dd), ikke tidspunkt. */
export type Sikring = Omit<
	NullableFields<
		GenSikring,
		// flyt er null for sikringer bestilt før flyten kunne velges. De gikk alle gjennom signering og kontrakt.
		// paaslag og oppfolgingsprodukt finnes bare på sikringer med kontrakt, bestilt etter at feltene kom.
		"prisomraade" | "volumKwh" | "volumPerMaanedKwh" | "innpris" | "fortjeneste" | "pris" | "flyt" | "paaslag" | "oppfolgingsprodukt"
	>,
	"volumPerMaanedKwh"
> & {
	volumPerMaanedKwh: Record<string, number> | null;
};

/** Ett secure volume for én kalendermåned, slik det sendes til UtilityCloud. */
export type Maanedsvolum = Required<GenMaanedsvolum>;

/** Bedriften som bestilte sikringen, kopiert fra ordrens kunde idet sikringsordren ble opprettet. */
export type Sikringskunde = NullableFields<GenSikringskunde, keyof GenSikringskunde>;

/** Kvalitetssikring av en sikringsordre, gjort av en annen enn den som opprettet den. */
export type Kvalitetssikring = Required<GenKvalitetssikring>;

/** En sikring og status for innsending av den til UtilityCloud. GET /sikringsordre. */
export interface Sikringsordre extends NullableFields<
	Omit<GenSikringsordre, "kunde" | "sikring" | "maanedsvolumer" | "status" | "kvalitetssikring">,
	// maalepunkter kan være null på eldre sikringsordrer, opprettet før ordren fikk en liste med målepunkt.
	// opprettetAv er null på sikringsordrer opprettet før backend begynte å lagre den.
	"produktId" | "feilaarsak" | "maalepunkter" | "opprettetAv"
> {
	status: Sikringsordrestatus;
	/** Null for sikringsordrer opprettet før backend begynte å lagre kunden. */
	kunde: Sikringskunde | null;
	sikring: Sikring;
	maanedsvolumer: Maanedsvolum[];
	/** Null dersom sikringsordren ikke er kvalitetssikret. */
	kvalitetssikring: Kvalitetssikring | null;
}

/** Kredittsjekk-resultatet i et BekreftOrdreResponse. */
export type Kredittsjekk = Required<GenKredittsjekkResponse>;

export interface BekreftOrdreResponse extends NullableFields<Omit<GenBekreftOrdreResponse, "kredittsjekk">, "samtykkeRedirect"> {
	kredittsjekk: Kredittsjekk | null;
}

/** Valideringsfeil for ett felt, slik den kommer fra ValidationErrors under en 400-respons. */
export type ValidationError = NullableFields<GenValidationError, keyof GenValidationError>;

/** 400-responsen fra backend ved valideringsfeil. */
export interface ValidationErrors {
	errors: ValidationError[];
}

/**
 * Et målepunkt en sikring på kundenivå vil omfatte. GET /sikringsordre/kundenivaa/maalepunkter.
 * Forventet årsforbruk er slik det var registrert i UtilityCloud da kontrakten ble opprettet, og kan mangle.
 * harSikringsprodukt er null for målepunkt lagt til for hånd, der det ikke er slått opp.
 */
export type SikringMaalepunkt = NullableFields<GenSikringMaalepunkt, "maalernummer" | "adresse" | "forventetAarsforbrukKwh" | "harSikringsprodukt">;
